import React, { useState, useEffect } from 'react';
import { statisticsAPI } from '../api/axios';
import Button from '../components/Button';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { TrendingUp, TrendingDown, Calendar, Users, DollarSign } from 'lucide-react';
import { formatCurrency } from '../utils/dateUtils';
import './DashboardPage.css';

/**
 * 매출 대시보드 페이지 (본문 컨텐츠만)
 * 사이드바와 헤더는 AdminLayout에서 제공
 */
const DashboardPage = () => {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await statisticsAPI.getDashboard();
      setStatistics(response.data);
      setError(null);
    } catch (err) {
      console.error('통계 조회 실패:', err);
      setError('통계 데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 파이 차트 색상 (파스텔 톤)
  const COLORS = ['#D4A574', '#C9A690', '#E8C4A0', '#B8947D', '#DDB892'];

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>통계 데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="error-message">
          <p>{error}</p>
          <Button onClick={fetchStatistics} variant="primary" size="medium">다시 시도</Button>
        </div>
      </div>
    );
  }

  // statistics 데이터가 없으면 로딩 상태 표시
  if (!statistics) {
    return (
      <div className="dashboard-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>통계 데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* 상단 요약 카드 */}
      <div className="summary-cards">
        <div className="summary-card revenue">
          <div className="card-icon">
            <DollarSign size={32} />
          </div>
          <div className="card-content">
            <p className="card-label">이번 달 매출</p>
            <h2 className="card-value">{formatCurrency(statistics.totalRevenue)}</h2>
            <div className={`growth-indicator ${statistics.monthlyGrowthRate >= 0 ? 'positive' : 'negative'}`}>
              {statistics.monthlyGrowthRate >= 0 ? (
                <TrendingUp size={16} />
              ) : (
                <TrendingDown size={16} />
              )}
              <span>{Math.abs(statistics.monthlyGrowthRate).toFixed(1)}%</span>
              <span className="growth-label">전월 대비</span>
            </div>
          </div>
        </div>

        <div className="summary-card bookings">
          <div className="card-icon">
            <Calendar size={32} />
          </div>
          <div className="card-content">
            <p className="card-label">예약 건수</p>
            <h2 className="card-value">{statistics.totalBookings}건</h2>
            <p className="card-description">이번 달 확정 예약</p>
          </div>
        </div>

        <div className="summary-card customers">
          <div className="card-icon">
            <Users size={32} />
          </div>
          <div className="card-content">
            <p className="card-label">신규 고객</p>
            <h2 className="card-value">{statistics.newCustomers}명</h2>
            <p className="card-description">이번 달 신규 등록</p>
          </div>
        </div>
      </div>

      {/* 메인 차트: 일별 매출 추이 */}
      <div className="chart-section">
        <h3 className="chart-title">📈 최근 30일 매출 추이</h3>
        <div className="chart-container">
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={statistics.dailySales}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#D4A574" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#D4A574" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date" 
                tick={{ fill: '#8B7355', fontSize: 12 }}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return `${date.getMonth() + 1}/${date.getDate()}`;
                }}
              />
              <YAxis 
                tick={{ fill: '#8B7355', fontSize: 12 }}
                tickFormatter={(value) => `${(value / 10000).toFixed(0)}만`}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: '#FFF8F0',
                  border: '1px solid #D4A574',
                  borderRadius: '8px'
                }}
                formatter={(value) => [formatCurrency(value), '매출']}
                labelFormatter={(label) => `날짜: ${label}`}
              />
              <Legend wrapperStyle={{ color: '#8B7355' }} />
              <Line 
                type="monotone" 
                dataKey="revenue" 
                stroke="#D4A574" 
                strokeWidth={3}
                fill="url(#colorRevenue)"
                name="일별 매출"
                dot={{ fill: '#D4A574', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 하단: 시술별 통계 */}
      <div className="bottom-section">
        {/* 도넛 차트 */}
        <div className="chart-section donut-section">
          <h3 className="chart-title">💆‍♀️ 시술별 점유율</h3>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statistics.popularTreatments}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  fill="#8884d8"
                  paddingAngle={5}
                  dataKey="bookingCount"
                  nameKey="treatmentName"
                  label={({treatmentName, percent}) => `${treatmentName} ${(percent * 100).toFixed(0)}%`}
                >
                  {statistics.popularTreatments.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#FFF8F0',
                    border: '1px solid #D4A574',
                    borderRadius: '8px'
                  }}
                  formatter={(value, name, props) => [
                    `${value}건 (${formatCurrency(props.payload.revenue)})`,
                    '예약 건수'
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 인기 시술 테이블 */}
        <div className="chart-section table-section">
          <h3 className="chart-title">🏆 인기 시술 Top 5</h3>
          <div className="treatments-table">
            <table>
              <thead>
                <tr>
                  <th>순위</th>
                  <th>시술명</th>
                  <th>예약 건수</th>
                  <th>매출</th>
                </tr>
              </thead>
              <tbody>
                {statistics.popularTreatments.map((treatment, index) => (
                  <tr key={index}>
                    <td className="rank">
                      <span className={`rank-badge rank-${index + 1}`}>
                        {index + 1}
                      </span>
                    </td>
                    <td className="treatment-name">{treatment.treatmentName}</td>
                    <td className="booking-count">{treatment.bookingCount}건</td>
                    <td className="revenue">{formatCurrency(treatment.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
